import React, { useCallback, useEffect, useRef, lazy, Suspense } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Routes, Route, BrowserRouter, Navigate } from 'react-router-dom';
import { MOBILE_BREAKPOINT } from './js/constants';
import { setIsMobile } from './actions';
import { verifyUser } from './actions/userAction';
import { syncNotificationsAfterReconnect } from './redux/state-areas/notifications/actions';

// Auth + shell — kept eager (always needed on load)
import SignIn from './components/UI/organisms/SignIn';
import SignUp from './components/UI/organisms/SignUp';
import GetStarted from './components/pages/GetStarted';
import Layout from './components/pages/Layout';
import NavigationInitializer from './NavigationInitializer';
import { SetupAccount } from './components/UI/organisms/SetupAccount';
import CreateWorkspace from './components/UI/organisms/CreateWorkspace';
import SelectWorkspace from './components/UI/organisms/SelectWorkspace';
import ResetPassword from './components/UI/organisms/ResetPassword';
import NewPassword from './components/UI/organisms/NewPassword';
import JoinWorkspace from './components/pages/JoinWorkspace';
import Home from './components/pages/Home';


// Lazy — standalone full-screen pages rarely visited
const Pricing = lazy(() => import('./components/pages/Pricing'));
const StripeWrapper = lazy(() => import('./components/UI/molecules/global/StripeWrapper'));
const PaymentSuccess = lazy(() => import('./components/pages/PaymentSuccess'));
const PaymentError = lazy(() => import('./components/pages/PaymentError'));
const PricingUsers = lazy(() => import('./components/pages/PricingUsers'));
const AllUsers = lazy(() => import('./components/pages/AllUsers'));
const PublicForm = lazy(() => import('./components/UI/organisms/PublicForm/PublicForm'));
const StripeCallback = lazy(() => import('./components/pages/StripeCallback'));
const PaymentsPage = lazy(() => import('./components/pages/PaymentsPage'));
const AdminConsoleLayout = lazy(() => import('./components/pages/AdminConsoleLayout'));
const Diagnostic = lazy(() => import('./components/pages/Diagnostic'));

// Lazy — in-Layout pages. Safe to split cause Layout has an inner
// <Suspense> (content-area fallback, nav stays).
const AccountManagement = lazy(() => import('./components/UI/organisms/AccountManagement'));
const OAuthConsent = lazy(() => import('./components/pages/OAuthConsent'));
const Integrations = lazy(() => import('./components/pages/Integrations'));
const Activity = lazy(() => import('./components/pages/Activity'));
const BrowseProjects = lazy(() => import('./components/pages/BrowseProjects'));
const CalendarComponent = lazy(() => import('./components/UI/organisms/CalendarComponent'));
const ChartTasksPage = lazy(() => import('./components/pages/ChartTasksPage'));
const Chat = lazy(() => import('./components/pages/Chat'));
const Dashboard = lazy(() => import('./src-new/pages/Dashboard'));
const DashboardContainer = lazy(() => import('./components/pages/DashboardContainer'));
const Dashboards = lazy(() => import('./components/pages/Dashboards'));
const FormDetails = lazy(() => import('./components/UI/organisms/ProjectForm/FormDetails'));
const GlobalFormsPage = lazy(() => import('./components/pages/GlobalFormsPage'));
const MyReminders = lazy(() => import('./components/pages/MyReminders'));
const Portfolio = lazy(() => import('./components/pages/Portfolio'));
const PortfolioDetails = lazy(() => import('./components/pages/PortfolioDetails'));
const Profile = lazy(() => import('./components/pages/Profile'));
const ProjectChat = lazy(() => import('./components/pages/ProjectChat'));
const ProjectCustomFieldsTab = lazy(() => import('./components/UI/organisms/ProjectCustomFields'));
const ProjectForm = lazy(() => import('./components/UI/organisms/ProjectForm'));
const ProjectOverview = lazy(() => import('./components/pages/ProjectOverview'));
const ProjectSettings = lazy(() => import('./components/UI/organisms/ProjectSettings'));
const Search = lazy(() => import('./components/pages/Search'));
const TaskProject = lazy(() => import('./components/pages/TaskProject'));
const WeekCalendar = lazy(() => import('./components/pages/WeekCalendar'));

function App() {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.userReducer);
  const lastSyncRef = useRef(null);
  const syncTimeoutRef = useRef(null);

  const workspaceId = localStorage.getItem('workspaceId');
  const token = localStorage.getItem('token');

  const performNotificationsSync = useCallback(async () => {
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
    }

    syncTimeoutRef.current = setTimeout(async () => {
      const now = Date.now();
      const lastSync = lastSyncRef.current;

      if (lastSync && now - lastSync < 5000) {
        console.log('Sync skipped. Previous one was only 5s ago');
        return;
      }

      console.log('Performing notifications sync');
      lastSyncRef.current = now;

      dispatch(syncNotificationsAfterReconnect());
    }, 2000);
  }, [dispatch]);

  useEffect(() => {
    const handleOnline = () => {
      console.log('Browser online event');
      performNotificationsSync();
    };

    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('online', handleOnline);

      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
    };
  }, [performNotificationsSync]);

  useEffect(() => {
    let timeout;
    const updateIsMobile = () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        dispatch(setIsMobile(document.documentElement.clientWidth < MOBILE_BREAKPOINT));
      }, 150);
    };

    localStorage.setItem('virtualizedListFeatureEnabled', true);
    dispatch(setIsMobile(document.documentElement.clientWidth < MOBILE_BREAKPOINT));
    console.info(`[App]: Workflo Web App page loaded or refreshed`);

    window.addEventListener('resize', updateIsMobile);
    return () => {
      window.removeEventListener('resize', updateIsMobile);
      clearTimeout(timeout);
    };
  }, []);

  const getPath = (dest) => {
    const userToBeVerified = new URLSearchParams(window.location.search).get(
      'verifyUser',
    );
    if (userToBeVerified) {
      console.log('----- userToBeVerified', userToBeVerified);
      const user = {
        _id: userToBeVerified,
        isVerified: true,
      };
      dispatch(verifyUser(user, () => { }));
    }
    if (user || token) {
      return `/${workspaceId}/${dest}`;
    }
    return '/';
  };

  return (
    <BrowserRouter>
      <NavigationInitializer />
      <Suspense fallback={<div style={{ minHeight: '100vh', background: '#f4f5fb' }} />}>
      <Routes>
        <Route
          path="/all-tasks"
          element={
            <Layout phase={'all-tasks'}>
              <Home />
            </Layout>
          }
        />
        <Route
          exact
          path="/home"
          element={
            <Layout phase="home">
              <Home />
            </Layout>
          }
        />
        <Route exact path="/get-started" element={<GetStarted />} />
        <Route
          exact
          path="/activity"
          element={
            <Layout phase="activity">
              <Activity />
            </Layout>
          }
        />
        <Route
          exact
          path="/reminders"
          element={
            <Layout phase="reminders">
              <MyReminders />
            </Layout>
          }
        />
        <Route
          path="/search"
          element={
            <Layout phase="search-results">
              <Search />
            </Layout>
          }
        />
        <Route exact path="/pricing" element={<Pricing />} />
        <Route exact path="/pricing/users" element={<PricingUsers />} />
        <Route exact path="/payment/success" element={<PaymentSuccess />} />
        <Route exact path="/payment/error" element={<PaymentError />} />
        <Route
          path="/browse-projects"
          element={
            <Layout phase="browse-projects">
              <BrowseProjects />
            </Layout>
          }
        />
        <Route
          exact
          path="/profile/:id"
          element={
            <Layout phase="profile">
              <Profile />
            </Layout>
          }
        />
        <Route
          exact
          path="/:projectid/board"
          element={
            <Layout phase="project">
              <TaskProject isBoardView />
            </Layout>
          }
        />
        <Route
          exact
          path="/calendar"
          element={
            <Layout phase="calendar">
              <WeekCalendar />
            </Layout>
          }
        />
        <Route
          exact
          path="/portfolio"
          element={
            <Layout phase="portfolio">
              <Portfolio />
            </Layout>
          }
        />
        <Route
          exact
          path="/forms"
          element={
            <Layout phase="forms">
              <GlobalFormsPage />
            </Layout>
          }
        />
        <Route
          exact
          path="/dashboard"
          element={
            <Layout phase="dashboard">
              <Dashboards />
            </Layout>
          }
        />
        <Route
          exact
          path="/dashboard/:dashboardId"
          element={
            <Layout phase="dashboard">
              <DashboardContainer />
            </Layout>
          }
        />
        <Route
          exact
          path="/dashboard/:dashboardId/chart-tasks"
          element={
            <Layout phase="dashboard">
              <ChartTasksPage />
            </Layout>
          }
        />
        <Route
          path="/report"
          element={
            <Layout phase="report">
              <Search isReport={true} />
            </Layout>
          }
        />
        <Route
          exact
          path="/portfolio/:portfolioId"
          element={
            <Layout phase="portfolio">
              <PortfolioDetails />
            </Layout>
          }
        />
        <Route
          exact
          path="/:projectid/list"
          element={
            <Layout phase="project">
              <TaskProject />
            </Layout>
          }
        />
        <Route
          exact
          path="/:projectid/chat"
          element={
            <Layout phase="project">
              <ProjectChat />
            </Layout>
          }
        />
        <Route
          exact
          path="/:projectid/:taskid"
          element={
            <Layout phase="project">
              <TaskProject />
            </Layout>
          }
        />
        <Route
          exact
          path="/:projectid/:taskid/:fullView"
          element={
            <Layout phase="project">
              <TaskProject />
            </Layout>
          }
        />
        <Route
          exact
          path="/:projectid/calendar"
          element={
            <Layout phase="project">
              <CalendarComponent />
            </Layout>
          }
        />
        <Route
          exact
          path="/:projectid/overview"
          element={
            <Layout phase="project">
              <ProjectOverview />
            </Layout>
          }
        />
        <Route
          exact
          path="/:projectid/form"
          element={
            <Layout phase="project">
              <ProjectForm />
            </Layout>
          }
        />
        <Route exact path="/forms/:publicFormId" element={<PublicForm />} />
        <Route exact path="/stripe/callback" element={<StripeCallback />} />
        <Route
          exact
          path="/payments"
          element={
            <Layout phase="payments">
              <PaymentsPage />
            </Layout>
          }
        />
        <Route
          exact
          path="/:projectid/form/:formId"
          element={
            <Layout phase="project">
              <FormDetails />
            </Layout>
          }
        />
        <Route
          exact
          path="/:projectid/settings"
          element={
            <Layout phase="project">
              <ProjectSettings />
            </Layout>
          }
        />
        <Route
          exact
          path="/:projectid/custom-fields"
          element={
            <Layout phase="project">
              <ProjectCustomFieldsTab />
            </Layout>
          }
        />
        <Route
          exact
          path="/chat/:chatId"
          element={
            <Layout phase="chat">
              <Chat />
            </Layout>
          }
        />
        {/* <Route exact path='/teams' element={
          <Layout phase='teams'>
            <Teams />
          </Layout>
        } /> */}
        <Route
          exact
          path="/account-management"
          element={
            <Layout phase="account-management">
              <AccountManagement />˝
            </Layout>
          }
        />
        <Route
          exact
          path="/integrations"
          element={
            <Layout phase="integrations">
              <Integrations />
            </Layout>
          }
        />
        <Route exact path="/create-workspace" element={<CreateWorkspace />} />
        <Route exact path="/select-workspace" element={<SelectWorkspace />} />
        <Route
          exact
          path={getPath('')}
          element={
            user?._id || token ? (
              <Layout phase="workspace">
                <Dashboard />
              </Layout>
            ) : (
              <Navigate to="/signin" />
            )
          }
        />
        <Route
          exact
          path="/invite"
          element={
            <Layout phase="workspace">
              <Dashboard />
            </Layout>
          }
        />
        <Route exact path="/join/:token" element={<JoinWorkspace />} />
        {/* OAuth consent for AI apps (Claude, ChatGPT, ...). Not wrapped in
            Layout: a standalone page like /signin. */}
        <Route exact path="/oauth/consent" element={<OAuthConsent />} />
        <Route exact path="/signin" element={<SignIn />} />
        <Route exact path="/signup" element={<SignUp />} />
        <Route exact path="/setup-account" element={<SetupAccount />} />
        <Route exact path="/resetpassword" element={<ResetPassword />} />
        <Route exact path="/createpassword" element={<NewPassword />} />
        <Route exact path="/payment" element={<StripeWrapper />} />
        <Route exact path="/admin-console/members" element={<AdminConsoleLayout phase="members" />} />
        <Route exact path="/admin-console/workspaces" element={<AdminConsoleLayout phase="workspaces" />} />
        <Route exact path="/admin/sales" element={<AllUsers />} />
        <Route exact path="/diagnostic" element={<Diagnostic />} />
        <Route
          path="*"
          element={
            user?._id || token ? (
              <Navigate to={getPath('')} replace />
            ) : (
              <Navigate to="/signin" />
            )
          }
        />
      </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

export default App;
